import { useEffect, useMemo, useState } from 'react';

import { Box, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';

import { calculateZoneFareBreakdown } from '../../../utils/zoneFareCalculator';
import type { ZonePricingInput } from '../../../utils/zoneFareCalculator';
import { formatMoney } from '../../../utils/currency';

import type { ServiceSchedulePricing, VehicleSchedulePricing } from '../zoneTypes';
import { validateZoneDurationMinutes } from '../zoneTypes';
import { ZoneDistanceNumberField, ZoneDurationMinutesField } from './ZonePricingNumberField';

interface Props {
  servicePricing: ServiceSchedulePricing[];
  vehiclePricing?: VehicleSchedulePricing[];
  scheduleType: 'ride_now' | 'ride_later';
  currency?: string;
  surgeMultiplier?: number;
}

type PreviewOption = { id: string; label: string; hubType: string; slug: string };

function schedulePricing(
  scheduleType: 'ride_now' | 'ride_later',
  pricing: { rideNow: ZonePricingInput; rideLater: ZonePricingInput },
) {
  return scheduleType === 'ride_now' ? pricing.rideNow : pricing.rideLater;
}

function resolveSelectedPreview(
  selectedId: string,
  options: PreviewOption[],
  servicePricing: ServiceSchedulePricing[],
  vehiclePricing: VehicleSchedulePricing[],
  scheduleType: 'ride_now' | 'ride_later',
) {
  const meta = options.find((o) => o.id === selectedId) || options[0];
  if (!meta) return null;

  if (meta.id.startsWith('v:')) {
    const vehicleTypeCode = meta.id.slice(2);
    const vehicle = vehiclePricing.find((v) => v.vehicleTypeCode === vehicleTypeCode);
    if (!vehicle) return null;
    return {
      ...meta,
      pricing: schedulePricing(scheduleType, vehicle),
    };
  }

  const serviceId = meta.id.slice(2);
  const service = servicePricing.find((s) => s.serviceId === serviceId);
  if (!service) return null;
  return {
    ...meta,
    pricing: schedulePricing(scheduleType, service),
  };
}

export default function ZoneFarePreview({
  servicePricing, vehiclePricing = [], scheduleType, currency, surgeMultiplier = 1,
}: Props) {
  const options: PreviewOption[] = useMemo(() => [
    ...vehiclePricing.map((v) => ({
      id: `v:${v.vehicleTypeCode}`,
      label: v.vehicleTypeName,
      hubType: 'transport',
      slug: 'transport',
    })),
    ...servicePricing.map((s) => ({
      id: `s:${s.serviceId}`,
      label: s.serviceName,
      hubType: s.hubType,
      slug: s.slug,
    })),
  ], [vehiclePricing, servicePricing]);

  const [selectedId, setSelectedId] = useState(options[0]?.id || '');
  const [distanceKm, setDistanceKm] = useState(5);
  const [durationMin, setDurationMin] = useState(15);

  useEffect(() => {
    setSelectedId((prev) => {
      if (prev && options.some((o) => o.id === prev)) return prev;
      return options[0]?.id || '';
    });
  }, [options]);

  const effectiveId = options.some((o) => o.id === selectedId)
    ? selectedId
    : (options[0]?.id || '');

  const row = resolveSelectedPreview(
    effectiveId,
    options,
    servicePricing,
    vehiclePricing,
    scheduleType,
  );

  const durationError = validateZoneDurationMinutes(durationMin);
  const effectiveDurationMin = durationError ? Math.max(1, durationMin) : durationMin;

  const fare = row?.pricing
    ? calculateZoneFareBreakdown({
      hubType: row.hubType,
      slug: row.slug,
      pricing: row.pricing,
      distanceKm,
      durationMin: effectiveDurationMin,
      surgeMultiplier,
    })
    : null;

  if (!options.length) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">Select services in Step 1 to preview fare calculation.</Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Fare preview</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Base fare + (distance − base distance) × per km + time × per min — then surge &amp; tax
      </Typography>

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            size="small"
            label="Service / Vehicle"
            value={effectiveId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            {options.map((o) => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={4}>
          <ZoneDistanceNumberField
            label="Distance (km)"
            value={distanceKm}
            onValueChange={setDistanceKm}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <ZoneDurationMinutesField
            label="Duration (min)"
            value={durationMin}
            onValueChange={setDurationMin}
          />
        </Grid>
      </Grid>

      {fare && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {row?.label} · {distanceKm} km · {durationMin} min
            {surgeMultiplier !== 1 ? ` · surge ×${surgeMultiplier}` : ''}
          </Typography>

          <Stack spacing={0.75} sx={{ mb: 1.5 }}>
            {fare.lines.length > 0 ? fare.lines.map((line) => (
              <Stack
                key={line.id}
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={2}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2">{line.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{line.formula}</Typography>
                </Box>
                <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
                  {formatMoney(line.amount, currency)}
                </Typography>
              </Stack>
            )) : (
              <Typography variant="body2" color="text.secondary">
                No fare components configured — set pricing on Ride Now / Ride Later steps.
              </Typography>
            )}

            {fare.lines.length > 0 && fare.minimumFareApplied && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Subtotal before minimum: {formatMoney(fare.subtotalBeforeMinimum, currency)}
              </Typography>
            )}
          </Stack>

          <Box
            sx={{
              pt: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="body2">
              Subtotal: <strong>{formatMoney(fare.subtotal, currency)}</strong>
            </Typography>
            <Typography variant="body2">
              Tax ({Math.round(fare.taxRate * 100)}%): <strong>{formatMoney(fare.tax, currency)}</strong>
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              Total: {formatMoney(fare.total, currency)}
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
